import React, { useEffect, useState } from 'react';
import { Plus, CheckCircle, XCircle } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import ConfirmModal from '../../components/ConfirmModal';
import { requisitionService, medicineService } from '../../api/services';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

const statusColors = { pending: 'yellow', approved: 'green', rejected: 'red', ordered: 'blue' };
const priorityColors = { low: 'gray', medium: 'blue', high: 'orange', urgent: 'red' };


function MedicineSearch({ value, onSelect }) {
  const [q, setQ] = React.useState(value || '');
  const [res, setRes] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = async (v) => {
    if (!v) { setRes([]); return; }
    try { const r = await medicineService.search(v); setRes(r.data.data || []); setOpen(true); } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <input value={q}
        onChange={e => { setQ(e.target.value); search(e.target.value); }}
        placeholder="Search medicine..."
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
      {open && res.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
          {res.map(m => (
            <button key={m._id} type="button" onClick={() => { onSelect(m); setQ(m.commercialName); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0">
              <p className="text-sm font-medium text-gray-800">{m.commercialName}</p>
              <p className="text-xs text-gray-400">{m.genericName}{m.strength ? ` · ${m.strength}` : ''}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RequisitionsPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const { register, handleSubmit, reset, control, watch, setValue } = useForm({
    defaultValues: { items: [{ medicineName: '', quantity: 1 }], priority: 'medium', notes: '' }
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter !== 'all') params.status = filter;
      const res = await requisitionService.getAll(params);
      setData(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch { toast.error('Failed to load requisitions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [page, filter]);
  useEffect(() => {
    medicineService.getAll({ limit: 200 }).then(r => setMedicines(r.data.data || []));
  }, []);

  const onSubmit = async (formData) => {
    setSaving(true);
    try {
      await requisitionService.create(formData);
      toast.success('Requisition submitted');
      setModalOpen(false);
      reset();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id) => {
    try {
      await requisitionService.approve(id);
      toast.success('Requisition approved');
      fetchData();
    } catch { toast.error('Failed to approve'); }
    finally { setConfirmAction(null); }
  };

  const handleReject = async (id) => {
    try {
      await requisitionService.reject(id);
      toast.success('Requisition rejected');
      fetchData();
    } catch { toast.error('Failed to reject'); }
    finally { setConfirmAction(null); }
  };

  const columns = [
    { key: 'requisitionNumber', label: 'REQ No.' },
    { key: 'requestedBy', label: 'Requested By', render: (v) => v?.name || '-' },
    { key: 'items', label: 'Items', render: (v) => v?.length + ' item(s)' },
    { key: 'priority', label: 'Priority', render: (v) => <Badge color={priorityColors[v] || 'gray'}>{v}</Badge> },
    { key: 'status', label: 'Status', render: (v) => <Badge color={statusColors[v] || 'gray'}>{v}</Badge> },
    { key: 'approvedBy', label: 'Approved By', render: (v) => v?.name || '-' },
    { key: 'createdAt', label: 'Date', render: (v) => toJalali(v) },
  ];

  return (
    <div>
      <Breadcrumb />
      <PageHeader
        title="Purchase Requisitions"
        subtitle="Request and approve purchase orders"
        actions={<Button onClick={() => { reset(); setModalOpen(true); }}><Plus size={16} /> New Requisition</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'pending', 'approved', 'rejected', 'ordered'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filter === s ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns} data={data} loading={loading}
        totalItems={total} page={page} pageSize={20} onPageChange={setPage}
        title="Requisitions" exportFilename="requisitions"
        actions={(row) => (
          <div className="flex items-center gap-1">
            {row.status === 'pending' && (
              <>
                <button onClick={() => setConfirmAction({ type: 'approve', id: row._id, label: row.requisitionNumber })}
                  className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Approve">
                  <CheckCircle size={15} />
                </button>
                <button onClick={() => setConfirmAction({ type: 'reject', id: row._id, label: row.requisitionNumber })}
                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Reject">
                  <XCircle size={15} />
                </button>
              </>
            )}
          </div>
        )}
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="New Purchase Requisition" size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Priority">
              <Select {...register('priority')}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Select>
            </FormField>
            <FormField label="Notes">
              <Input {...register('notes')} placeholder="Additional notes" />
            </FormField>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Requested Items</label>
              <Button type="button" variant="outline" size="sm" onClick={() => append({ medicineName: '', quantity: 1 })}>
                <Plus size={14} /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field, idx) => (
                <div key={field.id} className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg">
                  <div className="col-span-2">
                    <MedicineSearch
                      value={watch(`items.${idx}.medicineName`) || ''}
                      onSelect={(med) => {
                        setValue(`items.${idx}.medicine`, med._id);
                        setValue(`items.${idx}.medicineName`, med.commercialName);
                        setValue(`items.${idx}.unit`, med.baseUnit || 'piece');
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" min={1} {...register(`items.${idx}.quantity`)} placeholder="Qty" />
                    {fields.length > 1 && (
                      <button type="button" onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 px-2">✕</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Submit Requisition</Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.type === 'approve' ? handleApprove(confirmAction.id) : handleReject(confirmAction.id)}
        variant={confirmAction?.type === 'approve' ? 'primary' : 'danger'}
        title={confirmAction?.type === 'approve' ? 'Approve Requisition' : 'Reject Requisition'}
        message={`Are you sure you want to ${confirmAction?.type} requisition "${confirmAction?.label}"?`}
        confirmLabel={confirmAction?.type === 'approve' ? 'Approve' : 'Reject'}
      />
    </div>
  );
}
